import type {
  SenderAddress,
  ShippingOptions,
  NotificationSettings,
  LabelFormat,
  LabelSize,
  DeliveryConfirmation,
} from '../types';

// EasyPost delivery confirmation options
export const DELIVERY_CONFIRMATION_OPTIONS: { value: DeliveryConfirmation | ''; label: string; description: string }[] = [
  { value: '', label: 'None', description: 'No signature required' },
  { value: 'NO_SIGNATURE', label: 'No Signature', description: 'Release package without signature' },
  { value: 'SIGNATURE', label: 'Signature', description: 'Requires signature from someone at delivery address' },
  { value: 'ADULT_SIGNATURE', label: 'Adult Signature', description: 'Requires signature from an adult (21+)' },
  { value: 'INDIRECT_SIGNATURE', label: 'Indirect Signature', description: 'Signature from address or neighbor (FedEx)' },
  { value: 'DIRECT', label: 'Direct Signature', description: 'Signature from someone at delivery address (FedEx)' },
  { value: 'SERVICE_DEFAULT', label: 'Service Default', description: 'Use carrier default based on service level' },
];

// EasyPost label format options
export const LABEL_FORMAT_OPTIONS: { value: LabelFormat; label: string; description: string }[] = [
  { value: 'PNG', label: 'PNG', description: 'Standard image format' },
  { value: 'PDF', label: 'PDF', description: 'Portable Document Format' },
  { value: 'ZPL', label: 'ZPL', description: 'Zebra Programming Language (4 x 6 thermal printer default)' },
  { value: 'EPL2', label: 'EPL2', description: 'Eltron Programming Language (thermal printers)' },
];

// EasyPost label size options
export const LABEL_SIZE_OPTIONS: { value: LabelSize; label: string; formats: LabelFormat[] }[] = [
  { value: '4x6', label: '4" x 6" (Standard)', formats: ['PNG', 'ZPL'] },
  { value: '8.5x11', label: '8.5" x 11" (Letter)', formats: ['PDF'] },
  { value: '4x5', label: '4" x 5"', formats: ['EPL2'] },
];

export function normalizeLabelCompatibility(options: ShippingOptions): ShippingOptions {
  const selectedSize = LABEL_SIZE_OPTIONS.find((option) => option.value === options.label_size);
  const compatibleSize = LABEL_SIZE_OPTIONS.find((option) => option.formats.includes(options.label_format));
  const normalized = selectedSize?.formats.includes(options.label_format) || !compatibleSize
    ? options
    : { ...options, label_size: compatibleSize.value };
  const allowedCustomCodes = new Set(['', 'PO', 'DP', 'RMA']);
  return {
    ...normalized,
    print_custom_1_code: allowedCustomCodes.has(normalized.print_custom_1_code) ? normalized.print_custom_1_code : '',
    print_custom_2_code: allowedCustomCodes.has(normalized.print_custom_2_code) ? normalized.print_custom_2_code : '',
  };
}

// Print custom code options supported by the FedEx-only deployment
export const PRINT_CUSTOM_CODES = [
  { value: '', label: 'None' },
  { value: 'PO', label: 'PO (Purchase Order)' },
  { value: 'DP', label: 'DP (Department)' },
  { value: 'RMA', label: 'RMA (Return Authorization)' },
];

export const defaultSenderAddress: SenderAddress = {
  name: 'Fifty 410',
  company: 'Fifty 410',
  street1: '14362 N Frank Lloyd Wright Blvd',
  street2: 'Suite 1000-327',
  city: 'Scottsdale',
  state: 'AZ',
  zip: '85260',
  country: 'US',
  phone: '(480) 624-2500',
  email: '',
};

export const defaultShippingOptions: ShippingOptions = {
  label_format: 'ZPL',
  label_size: '4x6',
  delivery_confirmation: null,
  insurance_enabled: false,
  default_insurance_amount: 0,
  saturday_delivery: false,
  hold_for_pickup: false,
  carbon_neutral: false,
  print_custom_1: '',
  print_custom_1_code: '',
  print_custom_2: '',
  print_custom_2_code: '',
};

export const defaultNotifications: NotificationSettings = {
  email_notifications: true,
  send_tracking_emails: true,
  tracking_email_template: 'default',
};
